import { Thermometer } from "lucide-react";
import { dictionaries } from "../lib/i18n";
import type { ClimateMonth, UiLang } from "../lib/types";

interface Props {
  climate: ClimateMonth;
  /** Måneden normalen gjelder, 1–12. Vises, ellers svarer tallene på ingenting. */
  month: number;
  lang: UiLang;
}

/**
 * Klimanormalen for stedet i reisemåneden.
 *
 * Ikke det samme som værvarselet over, og forskjellen er hele poenget:
 * varselet gjelder den dagen man reiser og rekker ni døgn fram. Normalen
 * gjelder måneden og bygger på tretti år. Den som planlegger i februar for juli
 * har bare normalen, og den er faktisk et svar — «normalt 9 til 19 grader» er
 * nok til å vite hva som skal i kofferten.
 *
 * Skydekket står med av en grunn som ikke er åpenbar: det er den ene faktoren
 * nordlys avhenger av som har en normal. Kp-verdien for en natt i februar
 * finnes ikke i september; andelen klare netter gjør det.
 */
export function ClimatePanel({ climate, month, lang }: Props) {
  const t = dictionaries[lang];
  const clearNights = Math.round(100 - climate.cloudPct);

  return (
    <div className="climate">
      <div className="climate-range">
        <Thermometer size={14} strokeWidth={2.2} />
        <strong>{t.climateRange(Math.round(climate.tMin), Math.round(climate.tMax))}</strong>
        <span className="climate-month">{t.monthName[month - 1]}</span>
      </div>

      <ul className="climate-facts">
        <li>
          <b>{climate.wetDays}</b>
          <span>{t.climateWetDays}</span>
        </li>
        <li>
          <b>{clearNights} %</b>
          <span>{t.climateClearNights}</span>
        </li>
        {climate.snowDays > 0 && (
          <li>
            <b>{climate.snowDays}</b>
            <span>{t.climateSnowDays}</span>
          </li>
        )}
        {climate.frostNights > 0 && (
          <li>
            <b>{climate.frostNights}</b>
            <span>{t.climateFrostNights}</span>
          </li>
        )}
      </ul>

      <p className="climate-note">{t.climateSource}</p>
    </div>
  );
}
